import { useCallback } from "react";
import { isResponseErrorArray } from "@monorepo/helpers";
import { useAddServiceToCartMutation, useUpdateCartMutation } from "@monorepo/services/api/carts";
import { ErrorCodeEnum, IAddServiceToCartResponse, ICart, IResponseError, IUpdateCartResponse } from "@monorepo/types";
import { SerializedError } from "@reduxjs/toolkit";

export type TUpdateConflictingServiceOnSuccess = ({
  cartGroupId,
  res,
}: {
  cartGroupId?: string;
  res?: IUpdateCartResponse;
}) => void;

interface IUseUpdateConflictingService {
  serviceId: string;
  serviceName: string;
  onSuccess: TUpdateConflictingServiceOnSuccess;
  onError: (err: Array<IResponseError>) => void;
  params: { [x: string]: string };
}

export const useUpdateConflictingService = ({
  serviceId,
  serviceName,
  params,
  onSuccess,
  onError,
}: IUseUpdateConflictingService) => {
  const [updateCart] = useUpdateCartMutation();
  const [addServiceToCart] = useAddServiceToCartMutation();

  const handleMutationResult = useCallback(
    (
      value:
        | { data: ICart | IAddServiceToCartResponse | IUpdateCartResponse }
        | { error: Array<IResponseError> | SerializedError }
    ) => {
      if ("data" in value) {
        onSuccess({ cartGroupId: params.cart_group_id, res: value.data as IUpdateCartResponse });
      } else if ("error" in value && isResponseErrorArray(value.error)) {
        onError(value.error);
      }
    },
    [onError, onSuccess, params.cart_group_id]
  );

  const updateConflictingService = useCallback(
    ({ dataDeclaration = false, error }: { dataDeclaration?: boolean; error: IResponseError }) => {
      if (+error.status === ErrorCodeEnum.CONFLICT && error?.source?.id) {
        updateCart({
          cartId: error.source.id,
          service_id: null,
          service_name: serviceName,
          cart_group_id: params.cart_group_id,
          data_declaration: dataDeclaration,
          params,
          barcode: null,
          pay_now: false,
          paid: false,
        }).then(handleMutationResult);
      } else {
        addServiceToCart({
          service_id: serviceId,
          service_name: serviceName,
          cart_group_id: params.cart_group_id,
          params,
          data_declaration: dataDeclaration,
          paid: true,
          pay_now: false,
        }).then(handleMutationResult);
      }
    },
    [addServiceToCart, handleMutationResult, params, serviceId, serviceName, updateCart]
  );
  return { updateConflictingService };
};
